import { useState } from 'react'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Calendar } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUser } from '@/contexts/UserContext'
import { useUsers } from '@/hooks/use-api'
import { format, startOfMonth, startOfQuarter, subMonths } from 'date-fns'
import { getFullFiscalYearRange, getCalendarYearRange } from '@/lib/utils'

export type DatePreset =
  | 'today'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'fiscal_year'
  | 'calendar_year'
  | 'last_90_days'
  | 'last_12_months'
  | 'all'
  | 'custom'

export interface ReportFilterValues {
  owner_id?: string
  startDate?: string
  endDate?: string
}

interface ReportFiltersProps {
  /** Which date presets to show (defaults to common set) */
  datePresets?: DatePreset[]
  /** Default date preset */
  defaultPeriod?: DatePreset
  /** Show the Period (date range) selector. Default true. Set false on views
   *  that don't support date filtering (e.g. forecast, leaderboards). */
  showPeriod?: boolean
  /** Callback when filters change */
  onChange: (filters: ReportFilterValues) => void
  /** Current filter values (controlled) */
  value: ReportFilterValues
}

const PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today',
  this_month: 'This Month',
  last_month: 'Last Month',
  this_quarter: 'This Quarter',
  fiscal_year: 'This Fiscal Year',
  calendar_year: 'This Calendar Year',
  last_90_days: 'Last 90 Days',
  last_12_months: 'Last 12 Months',
  all: 'All Time',
  custom: 'Custom Range',
}

export function getDateRange(preset: DatePreset): { startDate?: string; endDate?: string } {
  const now = new Date()
  const today = format(now, 'yyyy-MM-dd')
  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today }
    case 'this_month':
      return { startDate: format(startOfMonth(now), 'yyyy-MM-dd'), endDate: today }
    case 'last_month': {
      const last = subMonths(now, 1)
      return {
        startDate: format(startOfMonth(last), 'yyyy-MM-dd'),
        endDate: format(new Date(last.getFullYear(), last.getMonth() + 1, 0), 'yyyy-MM-dd'),
      }
    }
    case 'this_quarter':
      return { startDate: format(startOfQuarter(now), 'yyyy-MM-dd'), endDate: today }
    case 'fiscal_year':
      // GRM's fiscal year runs Mar 1 → end of Feb. Full year (future-inclusive)
      // so it matches the Salesforce fiscal-year reports.
      return getFullFiscalYearRange(now)
    case 'calendar_year':
      return getCalendarYearRange(now)
    case 'last_90_days':
      return { startDate: format(subMonths(now, 3), 'yyyy-MM-dd'), endDate: today }
    case 'last_12_months':
      return { startDate: format(subMonths(now, 12), 'yyyy-MM-dd'), endDate: today }
    case 'all':
    default:
      return {}
  }
}

export function ReportFilters({
  datePresets = [
    'this_month',
    'last_month',
    'this_quarter',
    'fiscal_year',
    'calendar_year',
    'all',
    'custom',
  ],
  defaultPeriod = 'this_month',
  showPeriod = true,
  onChange,
  value,
}: ReportFiltersProps) {
  const { currentUser } = useUser()
  const isAdmin = currentUser?.user_type === 'Admin' || currentUser?.user_type === 'Super Admin'
  const { data: usersData } = useUsers({ limit: 10000 })
  const [period, setPeriod] = useState<DatePreset>(defaultPeriod)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const salesUsers = (usersData?.data || [])
    .filter((u: any) => ['User', 'Admin', 'Super Admin'].includes(u.user_type) && u.is_active)
    .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))

  const handlePeriodChange = (newPeriod: DatePreset) => {
    setPeriod(newPeriod)
    if (newPeriod === 'custom') return // wait for date inputs
    const dates = getDateRange(newPeriod)
    // Always overwrite both date fields (not spread) so a preset that clears
    // them — e.g. "All Time", which returns {} — actually resets the previous
    // range instead of leaving the old startDate/endDate in place.
    onChange({ ...value, startDate: dates.startDate, endDate: dates.endDate })
  }

  const handleCustomDateChange = (field: 'from' | 'to', dateValue: string) => {
    const newFrom = field === 'from' ? dateValue : customFrom
    const newTo = field === 'to' ? dateValue : customTo
    if (field === 'from') setCustomFrom(dateValue)
    if (field === 'to') setCustomTo(dateValue)
    onChange({
      ...value,
      startDate: newFrom || undefined,
      endDate: newTo || undefined,
    })
  }

  const handleOwnerChange = (ownerId: string) => {
    onChange({
      ...value,
      owner_id: ownerId === 'all' ? undefined : ownerId,
    })
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/* Admins pick any rep; non-admins get an All Data / My Data scope toggle
          (the backend returns org-wide data when no owner_id is supplied). */}
      {isAdmin ? (
        <div>
          <Label htmlFor="report-owner-filter" className="text-xs text-muted-foreground mb-1 block">
            Sales Rep
          </Label>
          <Select value={value.owner_id || 'all'} onValueChange={handleOwnerChange}>
            <SelectTrigger
              id="report-owner-filter"
              className="w-[200px] h-9"
              aria-label="Filter by sales rep"
            >
              <SelectValue placeholder="All Reps" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reps</SelectItem>
              {salesUsers.map((user: any) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        currentUser?.id && (
          <div>
            <Label
              htmlFor="report-scope-filter"
              className="text-xs text-muted-foreground mb-1 block"
            >
              Data
            </Label>
            <Select value={value.owner_id || 'all'} onValueChange={handleOwnerChange}>
              <SelectTrigger
                id="report-scope-filter"
                className="w-[160px] h-9"
                aria-label="Filter by data scope"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Data</SelectItem>
                <SelectItem value={currentUser.id}>My Data</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )
      )}

      {/* Date Period */}
      {showPeriod && (
        <div>
          <Label
            htmlFor="report-period-filter"
            className="text-xs text-muted-foreground mb-1 block"
          >
            Period
          </Label>
          <Select value={period} onValueChange={(v) => handlePeriodChange(v as DatePreset)}>
            <SelectTrigger
              id="report-period-filter"
              className="w-[180px] h-9"
              aria-label="Select time period"
            >
              <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {datePresets.map((preset) => (
                <SelectItem key={preset} value={preset}>
                  {PRESET_LABELS[preset]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Custom Date Range */}
      {showPeriod && period === 'custom' && (
        <>
          <div>
            <Label htmlFor="report-from-date" className="text-xs text-muted-foreground mb-1 block">
              From
            </Label>
            <DatePicker
              id="report-from-date"
              value={customFrom}
              onChange={(v) => handleCustomDateChange('from', v ?? '')}
              className="h-9 w-[150px]"
              aria-label="Start date"
            />
          </div>
          <div>
            <Label htmlFor="report-to-date" className="text-xs text-muted-foreground mb-1 block">
              To
            </Label>
            <DatePicker
              id="report-to-date"
              value={customTo}
              onChange={(v) => handleCustomDateChange('to', v ?? '')}
              className="h-9 w-[150px]"
              aria-label="End date"
            />
          </div>
        </>
      )}
    </div>
  )
}
